import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Form, Modal, Button, Alert } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useDarkMode, useApiData } from 'Hooks';
import { getReactSelectStyles } from 'Helpers/UiHelper';
import { SettingsApiService } from 'Services/ApiServices';
import { JOB_TITLES_LIST } from 'Constants/JobTitlesConstants';
import PersonalInfoSection from './PersonalInfoSection';
import StaffInfoSection from './StaffInfoSection';
import FlightInfoSection from './FlightInfoSection';
import AdditionalOptionsSection from './AdditionalOptionsSection';

// Constants moved outside component to avoid exhaustive-deps warnings
const SOURCE_MARKET_LIST = [
  'TUI GAS',
  'TUI Poland',
  'TUI UK&I',
  'TUI Denmark',
  'TUI Finland',
  'TUI Norway',
  'TUI Sweden',
  'TUI Netherlands',
  'TUI Belgium',
  'TUI UK&I - Other',
  'TUI Czech Republic',
  'TUI France',
  'TUI Italy',
  'TUI Spain',
  'TUI Hungary',
];

const TYPE_OF_FLIGHT_LIST = ['Holiday', 'Visa', 'Training', 'Sick-leave / absence', 'Welfare'];

const NewFlightTicketModal = (props) => {
  const {
    show,
    onHide,
    newManualFlightTicket,
    setNewManualFlightTicket,
    newManualFlightTicketErrors,
    setNewManualFlightTicketErrors,
    onSubmit,
  } = props;

  const { darkMode } = useDarkMode();
  const { data: settings } = useApiData(() => SettingsApiService.getAll());
  const seasonsOptionList = useSelector((state) => state.masterData.seasons);
  const countriesOptionList = useSelector((state) => state.masterData.countries);
  const destinationsOptionList = useSelector((state) => state.masterData.destinations);

  const settingsKeywords = settings?.keywords || [];
  const airports = settingsKeywords
    .filter((keyword) => keyword.dropdownName === 'airports')
    .flatMap((keyword) => keyword.options.map((option) => ({ value: option.value, label: option.label })));

  const mappedAirports = airports.map((airport) => ({
    value: airport.value,
    label: `${airport.value} - ${airport.label}`,
  }));

  const luggageList = settingsKeywords
    .filter((keyword) => keyword.dropdownName === 'staff_luggage')
    .flatMap((keyword) => keyword.options.map((option) => ({ value: option.value, label: option.label })));

  const options = useMemo(
    () => ({
      sourceMarkets: SOURCE_MARKET_LIST.map((item) => ({ label: item, value: item })),
      seasons: seasonsOptionList
        .filter((season) => season.value?.toLowerCase().indexOf('tbc') === -1 && season.value)
        .sort((a, b) => a.value.localeCompare(b.value)),
      countries: countriesOptionList.filter((country) => country.value).sort((a, b) => a.value.localeCompare(b.value)),
      destinations: destinationsOptionList
        .filter(
          (destination) =>
            destination.value?.toLowerCase().indexOf('late starters') === -1 &&
            destination.value?.toLowerCase().indexOf('standby') === -1 &&
            destination.value
        )
        .sort((a, b) => a.value.localeCompare(b.value)),
      jobTitles: JOB_TITLES_LIST.map((item) => ({ label: item, value: item })),
      typeOfFlight: TYPE_OF_FLIGHT_LIST.map((item) => ({ label: item, value: item })),
      luggage: luggageList,
      airports: mappedAirports,
    }),
    [seasonsOptionList, countriesOptionList, destinationsOptionList, mappedAirports, luggageList]
  );

  const handleChange = (updates) => {
    setNewManualFlightTicket({ ...newManualFlightTicket, ...updates });
  };

  const hasErrors = Object.keys(newManualFlightTicketErrors).length > 0;

  return (
    <Modal size="lg" show={show} onHide={onHide} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-plus-circle me-2"></i>
          Create New Ticket
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {hasErrors && (
          <Alert variant="danger" className="mb-3">
            <i className="fa fa-exclamation-triangle me-2"></i>
            Please fix the errors below before submitting
          </Alert>
        )}
        <Form>
          <PersonalInfoSection
            data={newManualFlightTicket}
            errors={newManualFlightTicketErrors}
            onChange={handleChange}
            onClearErrors={() => setNewManualFlightTicketErrors({})}
          />
          <StaffInfoSection
            data={newManualFlightTicket}
            errors={newManualFlightTicketErrors}
            onChange={handleChange}
            onClearErrors={() => setNewManualFlightTicketErrors({})}
            options={options}
            getReactSelectStyles={(hasError) => getReactSelectStyles(darkMode, hasError)}
          />
          <FlightInfoSection
            data={newManualFlightTicket}
            errors={newManualFlightTicketErrors}
            onChange={handleChange}
            options={options}
            getReactSelectStyles={(hasError) => getReactSelectStyles(darkMode, hasError)}
          />
          <AdditionalOptionsSection
            data={newManualFlightTicket}
            errors={newManualFlightTicketErrors}
            onChange={handleChange}
            airports={mappedAirports}
            getReactSelectStyles={(hasError) => getReactSelectStyles(darkMode, hasError)}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide}>
          <i className="fa fa-times me-1"></i>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={() => onSubmit(newManualFlightTicket)}>
          <i className="fa fa-check me-1"></i>
          Create Ticket
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

NewFlightTicketModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  newManualFlightTicket: PropTypes.object.isRequired,
  setNewManualFlightTicket: PropTypes.func.isRequired,
  newManualFlightTicketErrors: PropTypes.object.isRequired,
  setNewManualFlightTicketErrors: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default NewFlightTicketModal;
